import React from 'react';
import { Button } from '../ui/button';
import { Move, Edit, Copy, Trash2, PanelTop, FileText, Mail } from 'lucide-react';

const CELL_SIZE = 20;
const CANVAS_PAD = 16; // container's p-4

/**
 * StandHoverMenu – action menu that appears ABOVE the hovered stand.
 * Uses onMouseEnter / onMouseLeave so LayoutEditor can keep it alive
 * while the cursor travels from the canvas to the menu.
 */
export function StandHoverMenu({
  hoveredStand,
  zoom,
  onMove,
  onEdit,
  onCopy,
  onDelete,
  onSides,
  onContract,
  onMail,
  onMouseEnter,
  onMouseLeave,
}) {
  if (!hoveredStand) return null;

  const statusInfo = [];
  if (hoveredStand.reserved_by_name && hoveredStand.status === 'rezerve')
    statusInfo.push(`Rezerve: ${hoveredStand.reserved_by_name}`);
  if (hoveredStand.sold_by_name && hoveredStand.status === 'satıldı')
    statusInfo.push(`Satan: ${hoveredStand.sold_by_name}`);

  // Position: horizontally centred on bounding-box, just above the top edge.
  // CANVAS_PAD (16px) is the container's padding that offsets the canvas.
  const standCentreX  = (hoveredStand.x + hoveredStand.width  / 2) * CELL_SIZE * zoom + CANVAS_PAD;
  const standTopY     = hoveredStand.y * CELL_SIZE * zoom + CANVAS_PAD;
  const menuW = 260; // approximate
  const menuH = 44;

  const leftPx = Math.max(4, standCentreX - menuW / 2);
  // If stand is near top, show below; otherwise above.
  const topPx  = standTopY >= menuH + 4
    ? standTopY - menuH - 4
    : (hoveredStand.y + hoveredStand.height) * CELL_SIZE * zoom + CANVAS_PAD + 4;

  return (
    <div
      className="absolute bg-white border-2 border-blue-500 rounded-lg shadow-xl p-1 flex flex-col gap-1"
      style={{ left: `${leftPx}px`, top: `${topPx}px`, zIndex: 100, pointerEvents: 'auto' }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {statusInfo.length > 0 && (
        <div className="px-1.5 py-0.5 text-[10px] text-gray-500 border-b border-gray-100">
          {statusInfo.map((info, i) => <div key={i}>{info}</div>)}
        </div>
      )}
      <div className="flex gap-1">
        <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); onMove(hoveredStand); }}
          data-testid="move-stand-btn" title="Taşı" className="h-8 w-8 p-0"><Move className="w-4 h-4" /></Button>
        <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); onEdit(hoveredStand); }}
          data-testid="edit-stand-btn" title="Düzenle" className="h-8 w-8 p-0"><Edit className="w-4 h-4" /></Button>
        <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); onCopy(hoveredStand); }}
          data-testid="copy-stand-btn" title="Kopyala" className="h-8 w-8 p-0"><Copy className="w-4 h-4" /></Button>
        <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); onSides(hoveredStand); }}
          data-testid="sides-stand-btn" title="Kenar Ayarları" className="h-8 w-8 p-0"><PanelTop className="w-4 h-4" /></Button>
        {(hoveredStand.status === 'rezerve' || hoveredStand.status === 'satıldı') && (
          <Button size="sm" variant="outline"
            onClick={(e) => { e.stopPropagation(); onContract && onContract(hoveredStand); }}
            data-testid="contract-stand-btn" title="Sözleşme Oluştur"
            className="h-8 w-8 p-0 text-teal-600 hover:border-teal-400"><FileText className="w-4 h-4" /></Button>
        )}
        {(hoveredStand.status === 'rezerve' || hoveredStand.status === 'satıldı') && hoveredStand.assigned_company_id && (
          <Button size="sm" variant="outline"
            onClick={(e) => { e.stopPropagation(); onMail && onMail(hoveredStand); }}
            data-testid="mail-stand-btn" title="E-posta Gönder"
            className="h-8 w-8 p-0 text-blue-600 hover:border-blue-400"><Mail className="w-4 h-4" /></Button>
        )}
        <Button size="sm" variant="destructive" onClick={(e) => { e.stopPropagation(); onDelete(hoveredStand.id); }}
          data-testid="delete-stand-btn" title="Sil" className="h-8 w-8 p-0"><Trash2 className="w-4 h-4" /></Button>
      </div>
    </div>
  );
}
